'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import useAppState from 'src/hooks/useAppState';
import useTeams from 'src/hooks/useTeams';
import useRouteDefs from 'src/hooks/useRouteDefs';
import { isNoMatch, ROUTE_ANCESTORS } from 'src/hooks/useRouteDefs';
import { styled } from '@mui/material/styles';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
import { Breadcrumbs } from 'src/components/Molecules/Breadcrumbs';
import { TopBar } from 'src/components/Organisms/TopBar';
import { Main } from 'src/components/Organisms/Main';
import { SplitPage } from 'src/components/Organisms/SplitPage';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Sidebar } from './components';
import useTranslations from 'src/hooks/useTranslations';
import { useRouteNames } from 'src/hooks/useRouteNames';
import Typography from '@mui/material/Typography';
import { BreadcrumbProvider, useBreadcrumbContext } from 'src/contexts/Breadcrumb';
import { ROUTES } from 'src/constants/routes';
interface ChromeProps {
  children: React.ReactNode;
  className?: string;
  /** Disables the sidebar and switches to a single-label breadcrumb. */
  disableSidebar?: boolean;
}
const ChromeContainer = styled(FlexRow)(() => ({
  position: 'relative',
  inset: 0,
  width: '100%',
  height: '100vh',
  padding: 0
}));
const ContentContainer = styled(FlexColumn)(() => ({
  flexGrow: 1,
  minHeight: 0,
  overflowX: 'auto',
  overflowY: 'hidden'
}));
const Chrome: React.FC<ChromeProps> = t0 => {
  const $ = _c(3);
  const {
    children,
    disableSidebar: t1
  } = t0;
  const disableSidebar = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== children || $[1] !== disableSidebar) {
    t2 = <BreadcrumbProvider><ChromeInner disableSidebar={disableSidebar}>{children}</ChromeInner></BreadcrumbProvider>;
    $[0] = children;
    $[1] = disableSidebar;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const ChromeInner: React.FC<ChromeProps> = ({
  children,
  disableSidebar = false
}) => {
  const pathname = usePathname();
  const {
    t,
    translationsUnavailable
  } = useTranslations();
  const ROUTE_NAMES = useRouteNames();
  const {
    labels
  } = useBreadcrumbContext();
  const {
    getCurrentRouteDef
  } = useRouteDefs();
  const isScreenSmall = useMediaQuery(theme => theme.breakpoints.down('lg'), {
    noSsr: true
  });
  const [open, setOpen] = useState(!isScreenSmall && !disableSidebar);
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([]);
  // Derived, not stored: the effect that set this only ever mirrored the current pathname's route
  // key, clearing it on no-match — which is exactly what this expression does.
  const currentRouteKey = useMemo(() => {
    if (!pathname) return '';
    const routeDef = getCurrentRouteDef(pathname);
    return isNoMatch(routeDef) ? '' : routeDef.key;
  }, [pathname, getCurrentRouteDef]);
  const {
    currentTeam
  } = useTeams();
  const {
    isLoading: isInitializing
  } = useAppState();
  useEffect(() => {
    if (disableSidebar) {
      // Derive the label from ROUTE_NAMES just like authenticated pages do, but
      // without a team prefix. Adding a translation key for a new route to
      // useRouteNames is all that is needed to update this breadcrumb.
      const pattern = ROUTES[currentRouteKey as keyof typeof ROUTES] as string | undefined;
      const label = (pattern ? ROUTE_NAMES[pattern as keyof typeof ROUTE_NAMES] : undefined) ?? '';
      // Assembles the breadcrumb labels. Not derivable as it stands: neither branch runs while
      // `currentTeam` is momentarily null, so the effect implicitly keeps the previous crumbs.
      // A `useMemo` would return [] instead and blank the bar on every team switch.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBreadcrumbs([label]);
      return;
    }
    if (currentTeam && currentRouteKey) {
      const ancestors = ROUTE_ANCESTORS[currentRouteKey] ?? [];
      const chain = [...ancestors, currentRouteKey];
      const crumbs = chain.map(key => {
        const pattern = ROUTES[key as keyof typeof ROUTES] as string | undefined;
        return labels[key] ?? (pattern ? ROUTE_NAMES[pattern as keyof typeof ROUTE_NAMES] : undefined) ?? '';
      }).filter(Boolean);
      setBreadcrumbs([currentTeam.name, ...crumbs]);
    }
  }, [currentTeam, currentRouteKey, disableSidebar, t, ROUTE_NAMES, labels]);
  useEffect(() => {
    if (currentTeam && !isScreenSmall && !disableSidebar) {
      // Reopens the sidebar when the team changes; `isScreenSmall` is deliberately excluded.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpen(true);
    }
    // Don't add isScreenSmall to the dependency array, as we only want to
    // trigger this effect more than once when the team changes.
    // `isScreenSmall` is deliberately excluded — see the comment inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam, disableSidebar]);
  const handleDrawerAction = (open: boolean) => {
    setOpen(open && !disableSidebar);
  };
  if (isInitializing && !disableSidebar) {
    return <SplitPage loadingMessage={t('components.switchCompany.loadingWorkspace')} isLoading={isInitializing} />;
  }
  return <ChromeContainer>
      <Head>
        <title>
          {currentTeam ? `${currentTeam.name} - ${ROUTE_NAMES[ROUTES[currentRouteKey as keyof typeof ROUTES] as keyof typeof ROUTE_NAMES] ?? currentRouteKey}` : 'Effektify One'}
        </title>
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      <Sidebar open={open} disabled={disableSidebar} handleDrawerAction={handleDrawerAction} />
      <ContentContainer>
        <TopBar open={open} handleDrawerAction={() => handleDrawerAction(!open)} disableDrawer={disableSidebar}>
          <Breadcrumbs items={breadcrumbs} />
          {translationsUnavailable && <FlexRow sx={{
          alignItems: 'center',
          gap: theme => theme.sizes.spacing['05'],
          flexGrow: 1,
          justifyContent: 'flex-end'
        }}>
              <WarningAmberOutlinedIcon />
              <Typography variant='heading-sm' sx={theme => ({
            color: theme.colors.text.primary,
            marginLeft: theme.sizes.spacing['01']
          })}>
                {/* The irony of having your message about the CMS not being available stored in the CMS. */}
                {t('common.technicalIssuesMessage')}
              </Typography>
            </FlexRow>}
          {currentTeam && currentTeam.logo_uri && <FlexColumn component='img' src={currentTeam.logo_uri} alt={`${currentTeam.name} logo`} sx={{
          height: 24,
          marginLeft: 'auto'
        }} />}
        </TopBar>
        <Main>
          {/* Main content area */}
          {children}
        </Main>
      </ContentContainer>
    </ChromeContainer>;
};
export default Chrome;
export type { ChromeProps };